/**
 * SupabaseTagsRepository
 * Responsabilidade: Acesso a dados de tags no Supabase
 * Princípios: Single Responsibility, Dependency Inversion
 */

import { supabase } from '../../lib/supabase';
import { Tag } from '../../types';

export class SupabaseTagsRepository {
    async findAll(organizationId: string): Promise<Tag[]> {
        const { data, error } = await supabase
            .from('tags')
            .select('id, name, color, created_at')
            .eq('organization_id', organizationId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('[TagsRepository] Error fetching tags:', error);
            throw new Error(`Erro ao buscar etiquetas: ${error.message}`);
        }

        return (data || []).map(this.mapToEntity);
    }

    async findById(id: string, organizationId: string): Promise<Tag | null> {
        const { data, error } = await supabase
            .from('tags')
            .select('id, name, color, created_at')
            .eq('id', id)
            .eq('organization_id', organizationId)
            .single();

        if (error) {
            if (error.code === 'PGRST116') return null;
            console.error('[TagsRepository] Error fetching tag:', error);
            throw new Error(`Erro ao buscar etiqueta: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async create(tag: Omit<Tag, 'id' | 'createdAt'>, organizationId: string): Promise<Tag> {
        const { data, error } = await supabase
            .from('tags')
            .insert([{
                organization_id: organizationId,
                name: tag.name,
                color: tag.color
            }])
            .select()
            .single();

        if (error) {
            console.error('[TagsRepository] Error creating tag:', error);
            throw new Error(`Erro ao criar etiqueta: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async update(id: string, tag: Partial<Tag>, organizationId: string): Promise<Tag> {
        const updateData: any = {};
        if (tag.name !== undefined) updateData.name = tag.name;
        if (tag.color !== undefined) updateData.color = tag.color;

        const { data, error } = await supabase
            .from('tags')
            .update(updateData)
            .eq('id', id)
            .eq('organization_id', organizationId)
            .select()
            .single();

        if (error) {
            console.error('[TagsRepository] Error updating tag:', error);
            throw new Error(`Erro ao atualizar etiqueta: ${error.message}`);
        }

        return this.mapToEntity(data);
    }

    async delete(id: string, organizationId: string): Promise<void> {
        const { error } = await supabase
            .from('tags')
            .delete()
            .eq('id', id)
            .eq('organization_id', organizationId);

        if (error) {
            console.error('[TagsRepository] Error deleting tag:', error);
            throw new Error(`Erro ao deletar etiqueta: ${error.message}`);
        }
    }

    private mapToEntity(data: any): Tag {
        return {
            id: data.id,
            name: data.name,
            color: data.color,
            createdAt: data.created_at
        };
    }
}

export const tagsRepository = new SupabaseTagsRepository();
